import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import {
  DateRange,
  SelectRangeEventHandler,
  SelectSingleEventHandler,
} from "react-day-picker";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

type SingleDateProps = {
  mode?: "single" | undefined;
  date: Date | undefined;
  setDate: SelectSingleEventHandler;
};

type RangeDateProps = {
  mode: "range";
  date: DateRange | undefined;
  setDate: SelectRangeEventHandler;
};

export function DatePicker({
  date,
  setDate,
  children,
  className,
  disabled,
  id,
  mode = "single",
}: {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  id?: string;
} & (SingleDateProps | RangeDateProps)) {
  const singleDateValue = (dt: Date) => format(dt, "dd/MM/yyyy");
  const rangeDateValue = ({ from, to }: DateRange) =>
    `${from ? singleDateValue(from) : ""} - ${to ? singleDateValue(to) : ""}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          disabled={disabled}
          variant={"outline"}
          className={cn(
            className,
            "justify-start text-left font-normal overflow-hidden",
            !date && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="whitespace-nowrap overflow-ellipsis overflow-hidden">
            {!date ? (
              <span>{children}</span>
            ) : mode === "single" ? (
              singleDateValue(date as Date)
            ) : (
              rangeDateValue(date as DateRange)
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          fromYear={1900}
          toYear={2100}
          captionLayout="dropdown-buttons"
          mode={mode}
          selected={date as never}
          onSelect={setDate as never}
          initialFocus
          classNames={{
            dropdown: "!bg-popover",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
